import { memo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import {
  ArrowDown,
  ArrowUp,
  Circle,
  EllipsisVertical,
  MessageSquare,
  Plus,
  Square,
  Star,
  Trash2,
} from 'lucide-react';

import Button from 'src/componentes/button';
import Popover from 'src/componentes/popover';
import css from 'src/utils/css';
import FormularioGlobalBufferedInput from './FormularioGlobalBufferedInput';
import FormularioGlobalQuestionBranching from './FormularioGlobalQuestionBranching';
import {
  BRANCHING_TARGET_TYPE,
  canConfigureQuestionBranching,
} from './formularioGlobalBranching';
import {
  FORMULARIO_GLOBAL_QUESTION_TYPES,
  getQuestionOptionLimit,
  getNextQuestionOptionValue,
  QUESTION_TYPES_WITH_EDITABLE_OPTIONS,
  QUESTION_TYPES_WITH_OPTION_COMMENT,
} from './FormularioGlobalQuestionTypes';
import CustomInput from 'src/componentes/CustomInput';

const classes = css`
  .options {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 14px;
  }

  .optionRow {
    align-items: flex-start;
    display: flex;
    gap: 10px;
  }

  .optionMarker {
    align-items: center;
    color: #64748b;
    display: inline-flex;
    flex: 0 0 auto;
    height: 34px;
    justify-content: center;
    width: 24px;
  }

  .optionInput {
    flex: 1;
  }

  .optionTitleRow {
    align-items: flex-start;
    display: flex;
    gap: 8px;
    min-width: 0;
  }

  .optionInputControl {
    flex: 1;
    min-width: 0;
  }

  .optionBadges {
    align-items: center;
    display: flex;
    flex: 0 0 50px;
    gap: 4px;
    justify-content: flex-end;
    min-height: 34px;
  }

  .optionBadge {
    align-items: center;
    background: #eef7f8;
    border: 1px solid #c7e3e7;
    border-radius: 6px;
    color: #0f7180;
    display: inline-flex;
    height: 22px;
    justify-content: center;
    width: 22px;
  }

  .optionBadgeRequired {
    background: #fff7ed;
    border-color: #fed7aa;
    color: #c2410c;
    font-size: 12px;
    font-weight: 800;
  }

  .optionActions {
    align-items: flex-start;
    display: flex;
    flex: 0 0 auto;
    gap: 4px;
  }

  .iconButton {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: #475569;
    display: inline-flex;
    flex: 0 0 auto;
    height: 34px;
    justify-content: center;
    padding: 0;
    width: 34px;

    &:not(:disabled):hover {
      background: #f1f5f9;
      color: #0f7180;
    }

    &:disabled {
      color: #cbd5e1;
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  .dropdownToggle {
    align-items: center;
    background: transparent !important;
    border: 0 !important;
    border-radius: 6px;
    box-shadow: none !important;
    color: #475569 !important;
    display: inline-flex;
    height: 34px;
    justify-content: center;
    padding: 0 !important;
    width: 34px;
  }

  .dropdownToggle:hover,
  .dropdownToggle:focus {
    background: #f1f5f9 !important;
    color: #0f7180 !important;
  }

  .dropdownMenu {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.16);
    min-width: 240px;
    padding: 10px 12px;
  }

  .dropdownTitle {
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
    margin-bottom: 8px;
    text-transform: uppercase;
  }

  .dropdownToggleRow {
    align-items: center;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    min-height: 34px;
  }

  .dropdownToggleRow + .dropdownToggleRow {
    border-top: 1px solid #f1f5f9;
    margin-top: 6px;
    padding-top: 8px;
  }

  .dropdownLabel {
    color: #334155;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 0;
  }

  .optionFooter {
    align-items: center;
    display: flex;
    gap: 12px;
    margin-left: 34px;
  }

  .limit {
    color: #64748b;
    font-size: 12px;
  }

  .nps {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 14px;
  }

  .npsOption {
    align-items: center;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    display: flex;
    gap: 3px;
    min-height: 34px;
    padding: 2px 3px 2px 8px;
  }

  .npsItem {
    align-items: center;
    color: #475569;
    display: inline-flex;
    font-size: 13px;
    font-weight: 700;
    height: 28px;
    justify-content: center;
    min-width: 30px;
  }
`;

const getOptionMarker = (tipo) => {
  if (tipo === FORMULARIO_GLOBAL_QUESTION_TYPES.MULTIPLA_ESCOLHA) {
    return <Square size={15} />;
  }

  if (tipo === FORMULARIO_GLOBAL_QUESTION_TYPES.ESCALA_ESTRELAS) {
    return <Star size={16} />;
  }

  return <Circle size={15} />;
};

const areOptionsEqual = (previousOptions = [], nextOptions = []) =>
  previousOptions.length === nextOptions.length &&
  previousOptions.every((previousOption, index) => {
    const nextOption = nextOptions[index];

    return (
      previousOption.id === nextOption?.id &&
      previousOption.texto === nextOption?.texto &&
      previousOption.titulo === nextOption?.titulo &&
      previousOption.valor === nextOption?.valor &&
      previousOption.destinoRamificacao === nextOption?.destinoRamificacao &&
      previousOption.destinoRamificacaoGuid ===
        nextOption?.destinoRamificacaoGuid &&
      previousOption.permitirComentario === nextOption?.permitirComentario &&
      previousOption.comentarioObrigatorio ===
        nextOption?.comentarioObrigatorio &&
      previousOption.ordem === nextOption?.ordem
    );
  });

const FormularioGlobalQuestionOptions = ({
  aplicarRamificacao = false,
  branchingTargets = [],
  questionKey,
  randomizarPerguntas,
  comentarioObrigatorio,
  disabled,
  exibirConfiguracaoComentarioOpcao = true,
  idPrefix,
  onChange,
  opcoes = [],
  permitirComentario,
  tipo,
}) => {
  const intl = useIntl();
  const optionIdPrefix = idPrefix || `option-${tipo}`;
  const branchingQuestionIndex = branchingTargets.findIndex(
    (target) =>
      target.tipo === BRANCHING_TARGET_TYPE.QUESTION &&
      target.key === questionKey
  );
  const nextBranchingTargets =
    branchingQuestionIndex < 0
      ? []
      : branchingTargets.slice(branchingQuestionIndex + 1);
  const showBranching =
    aplicarRamificacao && canConfigureQuestionBranching(tipo);
  const optionLimit = getQuestionOptionLimit(tipo);
  const isEditable = QUESTION_TYPES_WITH_EDITABLE_OPTIONS.includes(tipo);
  const canConfigureComment =
    permitirComentario &&
    exibirConfiguracaoComentarioOpcao &&
    QUESTION_TYPES_WITH_OPTION_COMMENT.includes(tipo);
  const canOrderOptions =
    isEditable || tipo === FORMULARIO_GLOBAL_QUESTION_TYPES.SIMNAO;
  const handleChangeOption = useCallback(
    (index, value) => {
      const nextOptions = [...opcoes];
      nextOptions[index] = {
        ...nextOptions[index],
        texto: value,
        titulo: value,
      };
      onChange(nextOptions);
    },
    [onChange, opcoes]
  );

  const handleAddOption = useCallback(() => {
    if (opcoes.length >= optionLimit) return;

    const nextOptionValue = getNextQuestionOptionValue(opcoes);

    onChange([
      ...opcoes,
      {
        id: 0,
        texto: intl.formatMessage(
          { id: 'labelOpcaoNumero' },
          { numero: opcoes.length + 1 }
        ),
        titulo: intl.formatMessage(
          { id: 'labelOpcaoNumero' },
          { numero: opcoes.length + 1 }
        ),
        valor: nextOptionValue,
        destinoRamificacao: null,
        destinoRamificacaoGuid: null,
        permitirComentario: !!permitirComentario,
        comentarioObrigatorio: !!permitirComentario && !!comentarioObrigatorio,
        ordem: opcoes.length + 1,
      },
    ]);
  }, [
    comentarioObrigatorio,
    intl,
    onChange,
    opcoes,
    optionLimit,
    permitirComentario,
  ]);

  const handleRemoveOption = useCallback(
    (index) => {
      if (opcoes.length <= 1) return;

      onChange(opcoes.filter((_, currentIndex) => currentIndex !== index));
    },
    [onChange, opcoes]
  );

  const handleMoveOption = useCallback(
    (index, direction) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= opcoes.length) return;

      const nextOptions = [...opcoes];
      const current = nextOptions[index];
      nextOptions[index] = nextOptions[targetIndex];
      nextOptions[targetIndex] = current;

      onChange(nextOptions);
    },
    [onChange, opcoes]
  );

  const handleToggleOptionComment = useCallback(
    (index, value) => {
      const nextOptions = [...opcoes];
      nextOptions[index] = {
        ...nextOptions[index],
        permitirComentario: value,
        comentarioObrigatorio: value
          ? !!nextOptions[index].comentarioObrigatorio
          : false,
      };
      onChange(nextOptions);
    },
    [onChange, opcoes]
  );

  const handleToggleRequiredComment = useCallback(
    (index, value) => {
      const nextOptions = [...opcoes];
      nextOptions[index] = {
        ...nextOptions[index],
        comentarioObrigatorio: !!nextOptions[index].permitirComentario && value,
      };
      onChange(nextOptions);
    },
    [onChange, opcoes]
  );

  const renderCommentIndicators = (opcao) => (
    <span className={classes.optionBadges}>
      {opcao.permitirComentario && (
        <>
          <span
            className={classes.optionBadge}
            title={intl.formatMessage({
              id: 'labelHabilitaComentario',
            })}
          >
            <MessageSquare size={13} />
          </span>
          {opcao.comentarioObrigatorio && (
            <span
              className={`${classes.optionBadge} ${classes.optionBadgeRequired}`}
              title={intl.formatMessage({
                id: 'labelComentarioObrigatorio',
              })}
            >
              *
            </span>
          )}
        </>
      )}
    </span>
  );

  const renderOptionCommentMenu = (opcao, index) =>
    canConfigureComment ? (
      <Popover
        align="start"
        className={classes.dropdownMenu}
        disabled={disabled}
        content={
          <>
            <div className={classes.dropdownTitle}>
              {intl.formatMessage({
                id: 'labelComentario',
              })}
            </div>
            <div className={classes.dropdownToggleRow}>
              <label
                className={classes.dropdownLabel}
                htmlFor={`${optionIdPrefix}-comment-${opcao.ordem || index}`}
              >
                {intl.formatMessage({
                  id: 'labelHabilitaComentario',
                })}
              </label>
              <CustomInput
                checked={!!opcao.permitirComentario}
                disabled={disabled}
                id={`${optionIdPrefix}-comment-${opcao.ordem || index}`}
                onChange={(event) =>
                  handleToggleOptionComment(index, event.target.checked)
                }
                type="switch"
              />
            </div>
            <div className={classes.dropdownToggleRow}>
              <label
                className={classes.dropdownLabel}
                htmlFor={`${optionIdPrefix}-comment-required-${
                  opcao.ordem || index
                }`}
              >
                {intl.formatMessage({
                  id: 'labelComentarioObrigatorio',
                })}
              </label>
              <CustomInput
                checked={
                  !!opcao.permitirComentario && !!opcao.comentarioObrigatorio
                }
                disabled={disabled || !opcao.permitirComentario}
                id={`${optionIdPrefix}-comment-required-${
                  opcao.ordem || index
                }`}
                onChange={(event) =>
                  handleToggleRequiredComment(index, event.target.checked)
                }
                type="switch"
              />
            </div>
          </>
        }
      >
        <button
          className={classes.dropdownToggle}
          disabled={disabled}
          title={intl.formatMessage({
            id: 'labelConfiguracoesComentario',
          })}
          type="button"
        >
          <EllipsisVertical size={17} />
        </button>
      </Popover>
    ) : null;

  const renderOptionCommentSettings = (opcao, index) => (
    <>
      {renderCommentIndicators(opcao)}
      {renderOptionCommentMenu(opcao, index)}
    </>
  );

  if (tipo === FORMULARIO_GLOBAL_QUESTION_TYPES.NPS) {
    return (
      <div className={classes.nps}>
        {opcoes.map((opcao, index) => (
          <div className={classes.npsOption} key={opcao.ordem}>
            <span className={classes.npsItem}>{opcao.titulo}</span>
            {canConfigureComment && renderOptionCommentSettings(opcao, index)}
          </div>
        ))}
      </div>
    );
  }

  if (!canOrderOptions) return null;

  return (
    <div className={`${classes.options} tw:@container`}>
      {opcoes.map((opcao, index) => (
        <div className={classes.optionRow} key={opcao.valor}>
          <span
            className={`${classes.optionMarker} ${
              showBranching && index === 0 ? 'tw:@2xl:translate-y-5' : ''
            }`}
          >
            {getOptionMarker(tipo)}
          </span>
          <div
            className={`${classes.optionInput} tw:flex tw:min-w-0 tw:flex-col tw:gap-2 tw:@2xl:flex-row`}
          >
            <div
              className={`tw:min-w-0 tw:flex-1 ${
                showBranching && index === 0 ? 'tw:@2xl:pt-5' : ''
              }`}
            >
              <div className={classes.optionTitleRow}>
                <div className={classes.optionInputControl}>
                  <FormularioGlobalBufferedInput
                    className={showBranching ? 'tw:mb-0' : undefined}
                    disabled={disabled || !isEditable}
                    onCommit={(value) => handleChangeOption(index, value)}
                    placeholder={intl.formatMessage(
                      { id: 'labelOpcaoNumero' },
                      { numero: index + 1 }
                    )}
                    value={opcao.texto || opcao.titulo}
                  />
                </div>
                {canConfigureComment && renderCommentIndicators(opcao)}
              </div>
            </div>
            {showBranching && (
              <FormularioGlobalQuestionBranching
                disabled={disabled}
                onChange={(branching) =>
                  onChange(
                    opcoes.map((option, optionIndex) =>
                      optionIndex === index
                        ? { ...option, ...branching }
                        : option
                    )
                  )
                }
                option={opcao}
                targets={nextBranchingTargets}
                randomizarPerguntas={randomizarPerguntas}
                showLabel={index === 0}
              />
            )}
          </div>
          <div
            className={`${classes.optionActions} ${
              showBranching && index === 0 ? 'tw:@2xl:pt-5' : ''
            }`}
          >
            {renderOptionCommentMenu(opcao, index)}
            <button
              className={classes.iconButton}
              disabled={disabled || index === 0}
              onClick={() => handleMoveOption(index, -1)}
              title={intl.formatMessage({
                id: 'labelMoverCima',
              })}
              type="button"
            >
              <ArrowUp size={17} />
            </button>
            <button
              className={classes.iconButton}
              disabled={disabled || index === opcoes.length - 1}
              onClick={() => handleMoveOption(index, 1)}
              title={intl.formatMessage({
                id: 'labelMoverBaixo',
              })}
              type="button"
            >
              <ArrowDown size={17} />
            </button>
            {isEditable && (
              <button
                className={classes.iconButton}
                disabled={disabled || opcoes.length <= 1}
                onClick={() => handleRemoveOption(index)}
                title={intl.formatMessage({
                  id: 'excluir',
                })}
                type="button"
              >
                <Trash2 size={17} />
              </button>
            )}
          </div>
        </div>
      ))}
      {showBranching && randomizarPerguntas && (
        <p className="tw:m-0 tw:text-xs tw:text-slate-500">
          {intl.formatMessage({ id: 'msgRandomizacaoDesativaRamificacao' })}
        </p>
      )}
      {isEditable && (
        <div className={classes.optionFooter}>
          <Button
            color="secondary"
            disabled={disabled || opcoes.length >= optionLimit}
            leftIcon={Plus}
            onClick={handleAddOption}
            size="sm"
          >
            {intl.formatMessage({
              id: 'labelAdicionarOpcao',
            })}
          </Button>
          <span className={classes.limit}>
            {intl.formatMessage(
              {
                id: 'labelLimiteOpcoesPergunta',
              },
              { total: opcoes.length, limite: optionLimit }
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default memo(
  FormularioGlobalQuestionOptions,
  (previousProps, nextProps) =>
    previousProps.aplicarRamificacao === nextProps.aplicarRamificacao &&
    previousProps.branchingTargets === nextProps.branchingTargets &&
    previousProps.questionKey === nextProps.questionKey &&
    previousProps.randomizarPerguntas === nextProps.randomizarPerguntas &&
    previousProps.disabled === nextProps.disabled &&
    previousProps.comentarioObrigatorio === nextProps.comentarioObrigatorio &&
    previousProps.exibirConfiguracaoComentarioOpcao ===
      nextProps.exibirConfiguracaoComentarioOpcao &&
    previousProps.idPrefix === nextProps.idPrefix &&
    previousProps.permitirComentario === nextProps.permitirComentario &&
    previousProps.tipo === nextProps.tipo &&
    areOptionsEqual(previousProps.opcoes, nextProps.opcoes)
);
